/**
 * The installation (hotel) this browser tab is working in, sent as the
 * x-installation-id header.
 *
 * Kept in memory rather than localStorage: localStorage is shared by every tab,
 * so with two hotel tabs open the last one loaded decided which hotel's data
 * the other tab received.
 */
let currentInstallationId = null;

/**
 * The InstallationProvider instance that set currentInstallationId. Only that
 * instance may clear it, so an unmount that React runs after a newer provider
 * has rendered can't wipe the newer provider's hotel.
 */
let currentOwner = null;

/**
 * The installation whose data is in the Apollo cache. Outlives
 * currentInstallationId (e.g. while on /installations), so switching hotel via
 * the selector still clears the previous hotel's cached queries.
 */
let cachedInstallationId = null;

/**
 * Last hotel opened in this browser. Only a fallback for pages outside a hotel
 * route (e.g. the mark-as-processed links in emails), whose API endpoints
 * require an installation id header. Hotel pages never read it.
 */
const LAST_INSTALLATION_KEY = "installationId";

/**
 * Root fields that are safe to keep across a hotel switch because their
 * arguments already identify the data, not the x-installation-id header.
 */
const KEEP_ROOT_FIELDS = new Set(["__typename", "installationByBrandingKey"]);

export function getCurrentInstallationId() {
  return currentInstallationId;
}

export function getLastInstallationId() {
  try {
    return localStorage.getItem(LAST_INSTALLATION_KEY);
  } catch (_) {
    return null;
  }
}

function rememberLastInstallationId(installationId) {
  try {
    localStorage.setItem(LAST_INSTALLATION_KEY, installationId);
  } catch (_) {
    // Storage unavailable (private mode, blocked): only the fallback is lost.
  }
}

/**
 * Drop cached root queries that were answered for another hotel. Many queries
 * (listBookings, listEvents, ...) have no installationId argument, so the cache
 * would otherwise return the previous hotel's results for the same variables.
 * broadcast: false keeps this safe to run during render; callers remount the
 * hotel's pages so they read the cleared cache.
 */
function evictInstallationScopedQueries(cache) {
  cache.modify({
    id: "ROOT_QUERY",
    fields: (value, { fieldName, DELETE }) => (KEEP_ROOT_FIELDS.has(fieldName) ? value : DELETE),
    broadcast: false,
  });
  cache.gc();
}

/**
 * Make `installationId` the tab's hotel, owned by `owner` (one token per
 * provider instance). Must run before the hotel's pages render so their first
 * requests carry the right header.
 */
export function activateInstallation(installationId, cache, owner = null) {
  if (!installationId) return;
  currentOwner = owner;
  if (installationId === currentInstallationId) return;
  if (cache && cachedInstallationId && cachedInstallationId !== installationId) {
    evictInstallationScopedQueries(cache);
  }
  cachedInstallationId = installationId;
  currentInstallationId = installationId;
  rememberLastInstallationId(installationId);
}

/**
 * Clear the tab's hotel when its provider unmounts, unless a newer provider
 * has already taken it over.
 */
export function releaseInstallation(owner) {
  if (currentOwner !== owner) return;
  currentInstallationId = null;
  currentOwner = null;
}

export function resetCurrentInstallationForTests() {
  currentInstallationId = null;
  currentOwner = null;
  cachedInstallationId = null;
}
